import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, getHotComics, getNewComics, getRecommendedComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

type TabKey = 'all' | 'hot' | 'new' | 'recommended';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'hot', label: '热门' },
  { key: 'new', label: '最新' },
  { key: 'recommended', label: '推荐' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>('all');

  const currentCategory = categories.find((c) => c.slug === slug);

  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabKey | null;
    if (tabParam && ['all', 'hot', 'new', 'recommended'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else {
      setActiveTab('all');
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug, activeTab]);

  const displayComics = useMemo(() => {
    if (!slug) return [];

    let source: Comic[];
    if (slug === 'all') {
      source = [...getComicsByCategory('love'), ...getComicsByCategory('fantasy'), ...getComicsByCategory('action'), ...getComicsByCategory('mystery'), ...getComicsByCategory('comedy'), ...getComicsByCategory('urban'), ...getComicsByCategory('isekai'), ...getComicsByCategory('bl')];
    } else {
      source = getComicsByCategory(slug);
    }

    switch (activeTab) {
      case 'hot':
        return source.filter((c) => c.isHot);
      case 'new':
        return source.filter((c) => c.isNew);
      case 'recommended':
        return source.filter((c) => c.isRecommended);
      default:
        return source;
    }
  }, [slug, activeTab]);

  if (!currentCategory && slug !== 'all') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">未找到该分类</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const displayName = slug === 'all' ? '全部漫画' : currentCategory?.name || '';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} - 韩漫网站`,
    description: `韩漫网站${displayName}分类，提供丰富的漫画免费韩漫资源，支持下拉式韩漫在线阅读。`,
    url: `https://www.hanman-site.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: displayComics.slice(0, 10).map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `https://www.hanman-site.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[
              { name: '首页', path: '/' },
              { name: '分类' },
              { name: displayName },
            ]} />
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}>
                {displayName}
              </h1>
              <p className="text-sm text-foreground-400 mt-1">
                共 {displayComics.length} 部作品
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-8 bg-background-100 rounded-full p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {displayComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {displayComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}